import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Table from 'Components/Table';
import { Text, Box, Flex, Icon, ICON_LIST } from 'Components/Base';
import TagItem from 'Components/TagItem';
import { MODULE_STATUS, RANK_STATUS, formatWatchedPercent } from 'Domains/module';
import { COLOR } from 'Components/Theme';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import Tooltip from 'Components/Tooltip';

import IndividualUnitDrawer from '../IndividualUnitDrawer';

const TextWrapper = styled(Box)`
  max-width: 250px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
`;

const AttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;
    padding: 5px 10px;
    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableCell-body {
    &:nth-last-child(-n + 2) {
      padding: 0;
      line-height: 2.6;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 5) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(1) {
        width: 250px;
      }

      &:nth-child(n + 3) {
        min-width: 87px;
        width: 87px;
      }
    }
  }
`;

const TKAttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;
    padding: 12px 16px;
    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableCell-body {
    &:nth-last-child(-n + 2) {
      padding: 0;
      line-height: 3.5;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      position: sticky;
      top: 0;
      z-index: 3;
      &:nth-child(n + 6) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      position: sticky;
      top: 49px !important;
      z-index: 3;
      &:nth-child(1) {
        width: 250px;
      }
      &:nth-child(-n + 5) {
        > div {
          position: relative;
          top: -25px;
        }
        &:nth-child(-n + 3) {
          > div {
            display: flex;
            justify-content: center;
          }
        }
      }

      &:nth-child(n + 4) {
        min-width: 87px;
        width: 87px;
      }
    }
  }
`;

const getBackgroundColor = rank => {
  if (rank === RANK_STATUS.GOOD) {
    return 'success';
  } else if (rank === RANK_STATUS.NORMAL) {
    return 'warning';
  } else if (rank === RANK_STATUS.NEED_IMPROVEMENT) {
    return 'dangerAlt';
  } else {
    return 'transparent';
  }
};

const TableHead = props => <Text fontWeight={600} {...props} />;
const HeaderColSpanText = styled(TableHead)`
  /* border-bottom: 1px solid ${({ theme }) => theme.colors.white}; */
  white-space: nowrap;
`;

// ◐ IN_PROGRESS is the same filled primary pill as ✓ COMPLETED, told apart only
// by the icon after the text, per spec. ✗ INCOMPLETED stays a grey outline pill.
const STATUS_TAG_STYLE = {
  [MODULE_STATUS.COMPLETED]: { tagColor: 'primary', textColor: 'white' },
  [MODULE_STATUS.IN_PROGRESS]: { tagColor: 'primary', textColor: 'white' },
};
const DEFAULT_STATUS_TAG_STYLE = { tagColor: 'greyscale.grey6', textColor: 'itemDisabled' };

const StatusCell = ({ cell: { value, row } }) => {
  const { t } = useTranslation('individualUnit');
  const isInProgress = value === MODULE_STATUS.IN_PROGRESS;
  const { tagColor, textColor } = STATUS_TAG_STYLE[value] ?? DEFAULT_STATUS_TAG_STYLE;
  const title = isInProgress
    ? `ดูไปแล้ว ${formatWatchedPercent(row.original?.watchedPercent)}`
    : undefined;
  return (
    <Flex justifyContent="center" title={title}>
      <TagItem py="1" px="2" tagColor={tagColor} justifyContent="center">
        <Text.Hint color={textColor} display="flex" alignItems="center">
          {t(value)}
          {isInProgress && (
            <Icon name={ICON_LIST.HALF_CIRCLE} style={{ marginLeft: '4px' }} color="white" />
          )}
        </Text.Hint>
      </TagItem>
    </Flex>
  );
};

const TKScore = ({ value, data, onClick }) => {
  const percentage = value.score / value.fullScore;
  if (!value || isNaN(percentage)) return null;

  return (
    <Flex
      style={{
        alignItems: 'center',
        cursor: 'pointer',
        justifyContent: 'center',
      }}
      onClick={() => onClick(data)}
      bg={getBackgroundColor(value.rank)}
    >
      <TextWrapper>
        <Box
          style={{
            borderBottom: `1px solid ${COLOR.buttonText}`,
            marginRight: '8px',
            lineHeight: '20px',
            display: 'inline-block',
          }}
        >{`${value.score}/${value.fullScore}`}</Box>
      </TextWrapper>

      <Icon name={ICON_LIST.EYE} size={16} color={COLOR.buttonText} />
    </Flex>
  );
};

export const IndividualUnitAttendanceTable = ({ data, maxHeight, mode, ...props }) => {
  const { t } = useTranslation('individualUnit');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const openDrawer = data => {
    setSelectedData(data);
    setDrawerOpen(true);
  };

  const columns = [
    {
      id: 'sectionName',
      Header: <TableHead>{t('SECTION')}</TableHead>,
      accessor: 'sectionName',
      Cell: ({ cell: { value } }) => (
        <Tooltip title={value}>
          <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
        </Tooltip>
      ),
    },
    {
      id: 'subsectionName',
      Header: <TableHead>{t('SUBSECTION')}</TableHead>,
      accessor: 'subsectionName',
      Cell: ({ cell: { value } }) => (
        <Tooltip title={value}>
          <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
        </Tooltip>
      ),
    },
    {
      id: 'contentType',
      Header: <TableHead>{t('CATEGORY')}</TableHead>,
      accessor: 'contentType',
      Cell: ({ cell: { value } }) => (
        <Tooltip title={value}>
          <TextWrapper style={{ textTransform: 'uppercase' }}>{value}</TextWrapper>
        </Tooltip>
      ),
    },
    {
      id: 'status',
      Header: <TableHead>{t('STATUS')}</TableHead>,
      accessor: 'status',
      Cell: StatusCell,
    },
    {
      id: 'examinationResult',
      Header: <HeaderColSpanText>{t('EXAMINATION_RESULT')}</HeaderColSpanText>,
      columns: [
        {
          id: 'examinationResultRate',
          Header: <TableHead>{t('EXAMINATION_SCORE')}</TableHead>,
          accessor: 'quizResult',
          Cell: ({ cell: { value = {} } }) => {
            const percentage = value.score / value.fullScore;
            if (!value || isNaN(percentage)) return null;
            return (
              <TextWrapper
                bg={getBackgroundColor(value.rank)}
              >{`${value.score}/${value.fullScore}`}</TextWrapper>
            );
          },
        },
        {
          id: 'examinationResultPercentage',
          Header: <TableHead>{t('EXAMINATION_PERCENTAGE')}</TableHead>,
          accessor: 'quizResult',
          Cell: ({ cell: { value = {} } }) => {
            const percentage = (value.score / value.fullScore) * 100;
            if (!value || isNaN(percentage)) return null;
            return <TextWrapper bg={getBackgroundColor(value.rank)}>{percentage}</TextWrapper>;
          },
        },
      ],
    },
  ];

  const tkColumns = [
    {
      id: 'sectionName',
      Header: <TableHead>{t('SECTION')}</TableHead>,
      headerAlign: 'left',
      accessor: 'sectionName',
      Cell: ({ cell: { value } }) => (
        <Tooltip title={value}>
          <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
        </Tooltip>
      ),
    },
    {
      id: 'missionName',
      Header: <TableHead>{t('MISSION')}</TableHead>,
      headerAlign: 'left',
      accessor: 'sectionGroupName',
      Cell: ({ cell: { value } }) => (
        <Tooltip title={value}>
          <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
        </Tooltip>
      ),
    },
    {
      id: 'subsectionName',
      Header: <TableHead>{t('SUBSECTION')}</TableHead>,
      headerAlign: 'left',
      accessor: 'subsectionName',
      Cell: ({ cell: { value } }) => (
        <Tooltip title={value}>
          <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
        </Tooltip>
      ),
    },
    {
      id: 'contentType',
      Header: <TableHead>{t('CATEGORY')}</TableHead>,
      accessor: 'contentType',
      Cell: ({ cell: { value } }) => (
        <TextWrapper style={{ textTransform: 'uppercase' }}>{value}</TextWrapper>
      ),
    },
    {
      id: 'status',
      Header: <TableHead>{t('STATUS')}</TableHead>,
      accessor: 'status',
      Cell: StatusCell,
    },
    {
      id: 'missionResult',
      Header: <HeaderColSpanText>{t('MISSION_RESULT')}</HeaderColSpanText>,
      columns: [
        {
          id: 'examinationResultRate',
          Header: <TableHead>{t('EXAMINATION_SCORE')}</TableHead>,
          accessor: 'quizResult',
          Cell: ({ cell: { value = {} }, row }) => {
            return <TKScore value={value} data={row.original} onClick={openDrawer} />;
          },
          style: ({ cell }) => ({
            background: getBackgroundColor(cell.value?.rank),
          }),
        },
        {
          id: 'examinationResultPercentage',
          Header: <TableHead>{t('PERCENTAGE')}</TableHead>,
          accessor: 'quizResult',
          Cell: ({ cell: { value = {} } }) => {
            const percentage = (value.score / value.fullScore) * 100;
            if (!value || isNaN(percentage)) return null;
            return (
              <TextWrapper bg={getBackgroundColor(value.rank)}>{percentage.toFixed(2)}</TextWrapper>
            );
          },
        },
      ],
    },
  ];

  return mode === SYLLABUS_MODE.TRADITIONAL ? (
    <AttendanceTableStyle
      dense
      stickyHeader
      columns={columns}
      data={data}
      style={{ maxHeight }}
      {...props}
    />
  ) : (
    <>
      <TKAttendanceTableStyle
        dense
        stickyHeader
        columns={tkColumns}
        data={data}
        style={{ maxHeight }}
        {...props}
      />
      <IndividualUnitDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        data={selectedData}
      />
    </>
  );
};

IndividualUnitAttendanceTable.propTypes = {
  data: PropTypes.array.isRequired,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.string,
};

export default IndividualUnitAttendanceTable;
